/**
 * Tenant mail forwarding settings (D96) — SSOT: tenants.settings.mail
 * @see docs/product/TENANT_EMAIL_FORWARDING.md
 */

import { z } from 'zod';
import {
  isPlatformReservedHostname,
  resolveTenantRootDomain,
} from '@shared/tenant-host/defaultRootDomain';

export const MAIL_ROUTING_STATUSES = ['unconfigured', 'pending_dns', 'active'] as const;
export type MailRoutingStatus = (typeof MAIL_ROUTING_STATUSES)[number];

export const SEND_AS_DNS_STATUSES = ['not_started', 'pending', 'verified'] as const;
export type SendAsDnsStatus = (typeof SEND_AS_DNS_STATUSES)[number];

export const tenantMailAddressSchema = z.object({
  local: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9_-]+$/),
  label: z.string().min(1).max(80),
  enabled: z.boolean(),
});

export type TenantMailAddress = z.infer<typeof tenantMailAddressSchema>;

/** Enterprise custom domain (TM-5 v1) — tenant-owned zone, guide-only DNS */
export const tenantMailCustomDomainSchema = z.object({
  domain: z.string().min(3).max(253),
  verifiedAt: z.string().datetime().optional(),
  routingStatus: z.enum(MAIL_ROUTING_STATUSES),
});

export type TenantMailCustomDomain = z.infer<typeof tenantMailCustomDomainSchema>;

const CUSTOM_DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/** Lowercase hostname without trailing dot */
export function normalizeMailDomain(domain: string): string {
  return domain.trim().toLowerCase().replace(/\.$/, '');
}

/** Reject platform zone hijack and invalid hostnames */
export function assertTenantCustomDomainAllowed(domain: string): void {
  const d = normalizeMailDomain(domain);
  if (!CUSTOM_DOMAIN_RE.test(d)) {
    throw new Error('도메인 형식이 올바르지 않습니다. 예: yourcafe.com');
  }
  if (isPlatformReservedHostname(d)) {
    throw new Error('플랫폼 도메인(inflomatrix.app)은 자기 도메인으로 등록할 수 없습니다.');
  }
}

export const tenantMailSettingsSchema = z.object({
  forwardTo: z.string().email().optional(),
  forwardVerifiedAt: z.string().datetime().optional(),
  addresses: z.array(tenantMailAddressSchema).min(1),
  routingStatus: z.enum(MAIL_ROUTING_STATUSES),
  sendAsDnsStatus: z.enum(SEND_AS_DNS_STATUSES),
  customDomain: tenantMailCustomDomainSchema.optional(),
});

export type TenantMailSettings = z.infer<typeof tenantMailSettingsSchema>;

export const patchTenantMailCustomDomainSchema = z
  .object({
    domain: z.string().min(3).max(253).optional(),
    verifiedAt: z.string().datetime().optional(),
    routingStatus: z.enum(MAIL_ROUTING_STATUSES).optional(),
  })
  .strict();

export const patchTenantMailSettingsSchema = z
  .object({
    forwardTo: z.string().email().optional(),
    addresses: z.array(tenantMailAddressSchema).optional(),
    routingStatus: z.enum(MAIL_ROUTING_STATUSES).optional(),
    sendAsDnsStatus: z.enum(SEND_AS_DNS_STATUSES).optional(),
    customDomain: patchTenantMailCustomDomainSchema.optional(),
  })
  .strict();

export type PatchTenantMailSettings = z.infer<typeof patchTenantMailSettingsSchema>;

export const DEFAULT_TENANT_MAIL_ADDRESSES: TenantMailAddress[] = [
  { local: 'orders', label: '주문·문의', enabled: true },
  { local: 'support', label: '지원', enabled: false },
];

export const DEFAULT_SPF_TEMPLATE = 'v=spf1 include:_spf.google.com ~all';

export function createDefaultTenantMailSettings(): TenantMailSettings {
  return {
    addresses: DEFAULT_TENANT_MAIL_ADDRESSES.map((a) => ({ ...a })),
    routingStatus: 'unconfigured',
    sendAsDnsStatus: 'not_started',
  };
}

/** `{local}@{tenantSlug}.{rootDomain}` */
export function buildTenantBrandMailAddress(
  tenantSlug: string,
  local: string,
  rootDomain?: string
): string {
  const slug = tenantSlug.trim().toLowerCase();
  const root = resolveTenantRootDomain(
    rootDomain ?? process.env.TENANT_ROOT_DOMAIN ?? process.env.VITE_ROOT_DOMAIN
  );
  return `${local}@${slug}.${root}`;
}

/** `{local}@{customDomain}` */
export function buildCustomDomainMailAddress(domain: string, local: string): string {
  const host = normalizeMailDomain(domain);
  return `${local.trim().toLowerCase()}@${host}`;
}

export function isCustomDomainMailActive(
  custom?: TenantMailCustomDomain
): custom is TenantMailCustomDomain & { verifiedAt: string } {
  return Boolean(
    custom?.domain &&
    custom.routingStatus === 'active' &&
    custom.verifiedAt &&
    custom.verifiedAt.trim()
  );
}

export interface TenantCompanyInfoSlice {
  email?: string;
  phone?: string;
  legalName?: string;
}

/**
 * Site contact / JSON-LD — precedence:
 * 1) custom domain active+verified → orders@{customDomain}
 * 2) subdomain routing active → orders@{slug}.{rootDomain}
 * 3) companyInfo.email (Genesis Intake)
 */
export function resolveOrgContactEmail(
  tenantSlug: string,
  mail: TenantMailSettings | undefined,
  companyInfo?: TenantCompanyInfoSlice
): string | undefined {
  const ordersEnabled = mail?.addresses.find((a) => a.local === 'orders' && a.enabled);

  if (ordersEnabled && mail && isCustomDomainMailActive(mail.customDomain)) {
    return buildCustomDomainMailAddress(mail.customDomain.domain, 'orders');
  }

  if (mail?.routingStatus === 'active' && ordersEnabled) {
    return buildTenantBrandMailAddress(tenantSlug, 'orders');
  }

  const fallback = companyInfo?.email?.trim();
  return fallback || undefined;
}

export function routingStatusLabelKo(status: MailRoutingStatus): string {
  switch (status) {
    case 'active':
      return '전달 중';
    case 'pending_dns':
      return 'DNS 설정 중';
    default:
      return '미설정';
  }
}
